import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Library, ShieldAlert } from "lucide-react";
import DareLibrarySheet from "@/components/live-room/DareLibrarySheet";

const CATS = [
  { key: "funny", label: "Funny", tone: "border-yellow-400 text-yellow-300 bg-yellow-400/10" },
  { key: "flirty", label: "Flirty", tone: "border-pink-400 text-pink-300 bg-pink-400/10" },
  { key: "naughty", label: "Naughty", tone: "border-orange-400 text-orange-300 bg-orange-400/10" },
  { key: "after_dark", label: "After Dark 18+", tone: "border-fuchsia-400 text-fuchsia-300 bg-fuchsia-400/10" },
];
const DURATIONS = [30, 60, 90, 120];
const DRINKS = [
  ["water", "Water"], ["soda", "Soda"], ["juice", "Juice"], ["mocktail", "Mocktail"],
  ["tea_or_coffee", "Tea / Coffee"], ["adult_beverage", "Adult beverage (21+)"], ["no_drink", "No drink — Pass only"],
];

/** Dare or Shot 🐼🥃 — host launcher. Entertainment only; never touches coins, Bamboo or gifts. */
export default function DareOrShotSheet({ open, onOpenChange, streamId }) {
  const [cats, setCats] = useState(["funny", "flirty"]);
  const [duration, setDuration] = useState(60);
  const [community, setCommunity] = useState(true);
  const [ack, setAck] = useState(false);
  const [drink, setDrink] = useState("water");
  const [busy, setBusy] = useState(false);
  const [libOpen, setLibOpen] = useState(false);

  useEffect(() => { if (!open) setAck(false); }, [open]);

  const toggle = (k) => setCats((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  const afterDark = cats.includes("after_dark");
  const valid = cats.length > 0 && (!afterDark || ack);

  const start = async () => {
    if (busy || !valid) return;
    setBusy(true);
    try {
      await api.post(`/live/${streamId}/dare/start`, { categories: cats, duration_s: duration, include_community: community, after_dark_ack: ack, drink });
      toast.success("Dare or Shot started! Spin to pick the first player.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Dare or Shot.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" data-testid="dare-launch-sheet" className="z-[120] bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 text-white rounded-t-2xl max-h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white text-sm uppercase tracking-widest flex items-center gap-2">
              <span className="text-lg leading-none dare-panda">🐼</span> Dare or Shot <span className="text-lg leading-none">🥃</span>
            </SheetTitle>
          </SheetHeader>

          <div className="mt-3 space-y-4 pb-3">
            <p className="text-xs text-zinc-400">Everyone on stage can play — co-hosts tap <em>Join</em> on the game card. Dare, Shot or Pass — Pass is always free and drinks are always optional.</p>

            <section>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Categories</div>
              <div className="grid grid-cols-2 gap-2">
                {CATS.map((c) => (
                  <button key={c.key} type="button" data-testid={`dare-cat-${c.key}`} onClick={() => toggle(c.key)}
                    className={`py-2.5 rounded-xl border text-sm transition-colors ${cats.includes(c.key) ? c.tone : "bg-white/5 border-white/10 text-zinc-300"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
              {afterDark && (
                <label className="mt-2 flex items-start gap-2 text-xs text-fuchsia-100 bg-fuchsia-500/10 border border-fuchsia-400/30 rounded-xl p-2.5 cursor-pointer" data-testid="dare-after-dark-ack">
                  <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 accent-fuchsia-400" data-testid="dare-after-dark-ack-input" />
                  <span><ShieldAlert size={12} className="inline mr-1 -mt-0.5" />Everyone on stage is 18+ and agrees to After Dark. No nudity or explicit acts — anyone can Pass at any time.</span>
                </label>
              )}
            </section>

            <section>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Timer per challenge</div>
              <div className="flex gap-2">
                {DURATIONS.map((d) => (
                  <button key={d} type="button" data-testid={`dare-duration-${d}`} onClick={() => setDuration(d)}
                    className={`px-3 py-1.5 rounded-full border text-xs ${duration === d ? "bg-yellow-400/10 border-yellow-400 text-yellow-300" : "bg-white/5 border-white/10"}`}>
                    {d}s
                  </button>
                ))}
              </div>
            </section>

            <section className="grid grid-cols-2 gap-2">
              <label className="text-xs text-zinc-300 space-y-1">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Your drink (optional)</span>
                <select data-testid="dare-host-drink" value={drink} onChange={(e) => setDrink(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-white">
                  {DRINKS.map(([k, l]) => <option key={k} value={k} className="bg-zinc-900">{l}</option>)}
                </select>
              </label>
              <button type="button" data-testid="dare-community-toggle" onClick={() => setCommunity((v) => !v)}
                className={`self-end py-2 rounded-lg border text-xs ${community ? "bg-emerald-500/10 border-emerald-400 text-emerald-300" : "bg-white/5 border-white/10 text-zinc-400"}`}>
                {community ? "Community dares ON" : "Only built-ins + my favorites"}
              </button>
            </section>

            <button type="button" data-testid="dare-open-library" onClick={() => setLibOpen(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-white/10 bg-white/5 text-sm text-zinc-200 hover:border-yellow-400/40">
              <Library size={15} /> Custom dares · favorites · submit your own
            </button>

            <button type="button" data-testid="dare-start-btn" onClick={start} disabled={!valid || busy}
              className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black font-bold py-3 rounded-full text-sm flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Start Dare or Shot"}
            </button>
          </div>
        </SheetContent>
      </Sheet>
      <DareLibrarySheet open={libOpen} onOpenChange={setLibOpen} />
    </>
  );
}
